import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, AlertCircle, Trash2, Repeat } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANIMAL_TYPES = [
  ['HORSE','Horse'],['COW','Cow'],['SHEEP','Sheep'],['GOAT','Goat'],
  ['PIG','Pig'],['CHICKEN','Chicken'],['DOG','Dog'],['CAMEL','Camel'],
  ['EXOTIC','Exotic'],['OTHER','Other'],
];

/**
 * "Add Appointment" dialog (Session 30).
 *
 * Two modes:
 *   - "Group count" (default) — provider just enters animal count + one service.
 *   - "Per animal" — provider lists individual animals and assigns a service
 *     per row. Total = sum of (priceOverride OR service.price) × quantity.
 */
export default function AddAppointmentDialog({ onCreated }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);

  // Form state
  const [clientMode, setClientMode] = useState('new'); // 'new' | 'existing'
  const [existingClientId, setExistingClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [horseCount, setHorseCount] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState('UNPAID');
  const [notes, setNotes] = useState('');

  // RW-BUILD-003 — recurrence
  const [repeat, setRepeat] = useState(false);
  const [recInterval, setRecInterval] = useState(1);
  const [recUnit, setRecUnit] = useState('WEEKS');
  const [recEndRule, setRecEndRule] = useState('COUNT');
  const [recCount, setRecCount] = useState(6);
  const [recEndDate, setRecEndDate] = useState('');
  const [recConflicts, setRecConflicts] = useState([]);

  // Line-items mode (Session 30)
  const [itemsMode, setItemsMode] = useState('group');     // 'group' | 'items'
  const [clientAnimals, setClientAnimals] = useState([]);  // from /dashboard/clients/{id}/horses
  const [items, setItems] = useState([]);                  // [{animalId, serviceId, priceCents?, notes}]
  const [newAnimalName, setNewAnimalName] = useState('');
  const [newAnimalType, setNewAnimalType] = useState('HORSE');
  const [addingAnimal, setAddingAnimal] = useState(false);

  // Duplicate-client detection
  const [dupeMatch, setDupeMatch] = useState(null);

  useEffect(() => {
    if (!open || !token) return;
    (async () => {
      try {
        const [s, c] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/services`, { headers }),
          axios.get(`${API_URL}/api/dashboard/clients`, { headers }),
        ]);
        setServices(s.data || []);
        setClients(c.data || []);
      } catch (err) { console.error('load failed', err); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pre-fill from existing client + load that client's animals
  useEffect(() => {
    if (clientMode !== 'existing' || !existingClientId) return;
    const c = clients.find((x) => x.id === existingClientId);
    if (c) {
      setClientName(c.clientName || c.name || '');
      setClientPhone(c.clientPhone || c.phone || '');
      setClientEmail(c.clientEmail || c.email || '');
      setLocationAddress(c.address || '');
      setDupeMatch(null);
    }
  }, [existingClientId, clientMode, clients]);

  // Load animals for resolved clientId (existing or freshly created)
  useEffect(() => {
    const cid = clientMode === 'existing' ? existingClientId : null;
    if (!cid) { setClientAnimals([]); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/dashboard/clients/${cid}/horses`, { headers }
        );
        setClientAnimals(data || []);
      } catch (_e) { setClientAnimals([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingClientId, clientMode]);

  // Live dupe lookup on new-client phone/email
  useEffect(() => {
    if (!open || clientMode !== 'new') { setDupeMatch(null); return; }
    const phone = clientPhone.trim();
    const email = clientEmail.trim();
    if (!phone && !email) { setDupeMatch(null); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/dashboard/clients/lookup`,
          { headers, params: { phone: phone || undefined, email: email || undefined } }
        );
        setDupeMatch(data?.match ? data.client : null);
      } catch (_err) { setDupeMatch(null); }
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPhone, clientEmail, clientMode, open]);

  const useDupeMatch = () => {
    if (!dupeMatch) return;
    setClientMode('existing');
    setExistingClientId(dupeMatch.id);
    setClientName(dupeMatch.name || '');
    setClientPhone(dupeMatch.phone || '');
    setClientEmail(dupeMatch.email || '');
    setLocationAddress(dupeMatch.address || '');
    setDupeMatch(null);
    toast.success('Using existing client');
  };

  const addLineItem = () => {
    setItems((prev) => [
      ...prev,
      { _key: Math.random().toString(36).slice(2), animalId: '', serviceId: serviceId || '', priceCents: '', notes: '' },
    ]);
  };

  const updateLineItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeLineItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const quickAddAnimal = async () => {
    const cid = existingClientId;
    if (!cid) {
      toast.error('Pick an existing client first to add animals');
      return;
    }
    if (!newAnimalName.trim()) {
      toast.error('Enter an animal name');
      return;
    }
    setAddingAnimal(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/dashboard/clients/${cid}/horses`,
        { clientId: cid, name: newAnimalName.trim(), animalType: newAnimalType },
        { headers },
      );
      setClientAnimals((prev) => [...prev, data]);
      // Attach to the first empty row, or add a new row pre-bound to this animal
      setItems((prev) => {
        const emptyIdx = prev.findIndex((it) => !it.animalId);
        if (emptyIdx >= 0) {
          const next = [...prev];
          next[emptyIdx] = { ...next[emptyIdx], animalId: data.id };
          return next;
        }
        return [...prev, { _key: Math.random().toString(36).slice(2), animalId: data.id, serviceId: serviceId || '', priceCents: '', notes: '' }];
      });
      setNewAnimalName('');
      toast.success('Animal added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add animal');
    } finally {
      setAddingAnimal(false);
    }
  };

  const reset = () => {
    setClientMode('new'); setExistingClientId('');
    setClientName(''); setClientPhone(''); setClientEmail('');
    setLocationAddress(''); setServiceId(''); setDate(''); setTime('09:00');
    setHorseCount(1); setPaymentStatus('UNPAID'); setNotes('');
    setItemsMode('group'); setItems([]); setClientAnimals([]);
    setNewAnimalName(''); setNewAnimalType('HORSE');
    setDupeMatch(null);
    setRepeat(false); setRecInterval(1); setRecUnit('WEEKS');
    setRecEndRule('COUNT'); setRecCount(6); setRecEndDate(''); setRecConflicts([]);
  };

  const itemsTotalCents = items.reduce((sum, it) => {
    const svc = services.find((s) => s.id === it.serviceId);
    const price = it.priceCents !== '' && it.priceCents != null ? Math.round(parseFloat(it.priceCents) * 100) : (svc?.priceCents || 0);
    return sum + (Number.isFinite(price) ? price : 0);
  }, 0);

  const submit = async (e, forceArg = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const wantExisting = clientMode === 'existing' && existingClientId;
    if (!wantExisting && (!clientName.trim() || !clientPhone.trim())) {
      toast.error('Enter client name and phone');
      return;
    }
    if (!serviceId || !date || !time) {
      toast.error('Pick a default service, date and time');
      return;
    }
    if (itemsMode === 'items') {
      if (!items.length) { toast.error('Add at least one animal line, or switch to count-only'); return; }
      if (items.some((it) => !it.serviceId)) { toast.error('Each line needs a service'); return; }
    }

    setSaving(true);
    try {
      const startDateTime = `${date}T${time}:00`;
      const payload = {
        ...(wantExisting ? { clientId: existingClientId } : {}),
        clientName: clientName.trim(),
        clientPhone: clientPhone.trim(),
        clientEmail: clientEmail.trim() || null,
        locationAddress: locationAddress.trim() || null,
        serviceId,
        startDateTime,
        horseCount: itemsMode === 'items'
          ? Math.max(items.length, 1)
          : (parseInt(horseCount, 10) || 1),
        paymentStatus,
        notes: notes.trim() || null,
      };
      if (itemsMode === 'items') {
        payload.appointmentItems = items.map((it) => {
          const out = {
            serviceId: it.serviceId,
            quantity: 1,
          };
          if (it.animalId) out.animalId = it.animalId;
          if (it.notes && it.notes.trim()) out.notes = it.notes.trim();
          if (it.priceCents !== '' && it.priceCents != null) {
            out.priceCents = Math.round(parseFloat(it.priceCents) * 100);
          }
          return out;
        });
      }

      // RW-BUILD-003 — recurring series branch
      if (repeat) {
        const recurrence = {
          interval: Math.max(1, parseInt(recInterval, 10) || 1),
          intervalUnit: recUnit,
          endRule: recEndRule,
        };
        if (recEndRule === 'DATE') {
          if (!recEndDate) { toast.error('Pick an end date'); setSaving(false); return; }
          recurrence.endDate = recEndDate;
        } else {
          recurrence.count = Math.max(1, parseInt(recCount, 10) || 1);
        }
        const recPayload = { ...payload, recurrence, force: forceArg };
        const { data } = await axios.post(`${API_URL}/api/dashboard/recurring/create`, recPayload, { headers });
        if (data.requiresConfirmation) {
          setRecConflicts(data.conflicts || []);
          toast.warning('Some dates conflict — review and confirm to continue');
          setSaving(false);
          return;
        }
        toast.success(`Recurring series created (${data.occurrenceCount} appointments)`);
        reset();
        setOpen(false);
        onCreated?.();
        return;
      }

      await axios.post(`${API_URL}/api/dashboard/manual-appointments`, payload, { headers });
      toast.success('Appointment added to your calendar');
      reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save appointment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-appointment-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Appointment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-appointment-dialog">
        <DialogHeader>
          <DialogTitle>Add Appointment</DialogTitle>
          <DialogDescription>
            Log a job — show on calendar + field view immediately. List individual animals if you want to charge different services per animal.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* Client */}
          <div className="space-y-2">
            <Label>Client</Label>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={clientMode === 'new' ? 'default' : 'outline'}
                onClick={() => { setClientMode('new'); setExistingClientId(''); }}
                data-testid="client-mode-new">New client</Button>
              <Button type="button" size="sm" variant={clientMode === 'existing' ? 'default' : 'outline'}
                onClick={() => setClientMode('existing')} disabled={!clients.length}
                data-testid="client-mode-existing">Existing client</Button>
            </div>
            {clientMode === 'existing' && (
              <Select value={existingClientId} onValueChange={setExistingClientId}>
                <SelectTrigger data-testid="existing-client-select"><SelectValue placeholder="Pick a client..." /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => {
                    const label = c.clientName || c.name || 'Client';
                    const phone = c.clientPhone || c.phone || '';
                    return (
                      <SelectItem key={c.id} value={c.id} data-testid={`existing-client-option-${c.id}`}>
                        {label}{phone ? ` · ${phone}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            <Input placeholder="Name" value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="client-name-input" />
            <Input placeholder="Phone" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} data-testid="client-phone-input" />
            <Input placeholder="Email (optional)" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} data-testid="client-email-input" />
            <Input placeholder="Job address (optional)" value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)} data-testid="client-address-input" />
            {clientMode === 'new' && dupeMatch && (
              <div className="flex items-start gap-2 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900" data-testid="duplicate-client-banner">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="text-sm flex-1">
                  <p className="font-medium">Existing client match found</p>
                  <p className="text-xs">
                    <strong>{dupeMatch.name}</strong>{dupeMatch.phone ? ` · ${dupeMatch.phone}` : ''}{dupeMatch.email ? ` · ${dupeMatch.email}` : ''}
                  </p>
                  <Button type="button" size="sm" variant="outline" className="mt-2"
                    onClick={useDupeMatch} data-testid="use-existing-client-btn">Use this client</Button>
                </div>
              </div>
            )}
          </div>

          {/* Service (default) */}
          <div>
            <Label>Default Service</Label>
            <Select value={serviceId} onValueChange={setServiceId}>
              <SelectTrigger data-testid="service-select"><SelectValue placeholder="Pick a service..." /></SelectTrigger>
              <SelectContent>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`service-option-${s.id}`}>
                    {s.name} · {s.durationMinutes}m{s.priceCents ? ` · $${(s.priceCents/100).toFixed(2)}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appointment-date-input" />
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="appointment-time-input" />
            </div>
          </div>

          {/* Animals section — toggle between group count and per-animal */}
          <div className="space-y-2 rounded-md border p-3" data-testid="animals-section">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-base">Animals for this appointment</Label>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant={itemsMode === 'group' ? 'default' : 'outline'}
                  onClick={() => setItemsMode('group')} data-testid="items-mode-group">Use count only</Button>
                <Button type="button" size="sm" variant={itemsMode === 'items' ? 'default' : 'outline'}
                  onClick={() => { setItemsMode('items'); if (!items.length) addLineItem(); }}
                  data-testid="items-mode-items">Per animal</Button>
              </div>
            </div>

            {itemsMode === 'group' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Animal count</Label>
                  <Input type="number" min={1} value={horseCount}
                    onChange={(e) => setHorseCount(e.target.value)}
                    data-testid="horse-count-input" />
                </div>
                <div className="text-xs text-muted-foreground self-end pb-2">
                  Using default service × count for this appointment.
                </div>
              </div>
            )}

            {itemsMode === 'items' && (
              <div className="space-y-2">
                {clientMode !== 'existing' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    Tip: pick an existing client above to choose from saved animals.
                  </p>
                )}

                {items.map((it, idx) => {
                  const svc = services.find((s) => s.id === it.serviceId);
                  const fallbackPrice = svc?.priceCents || 0;
                  return (
                    <div key={it._key} className="border rounded p-2 space-y-2" data-testid={`item-row-${idx}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                        <div className="sm:col-span-5">
                          <Label className="text-xs">Animal</Label>
                          <Select
                            value={it.animalId || '__none__'}
                            onValueChange={(v) => updateLineItem(idx, { animalId: v === '__none__' ? '' : v })}
                          >
                            <SelectTrigger data-testid={`item-animal-select-${idx}`}><SelectValue placeholder="Pick animal" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Unlabeled —</SelectItem>
                              {clientAnimals.map((a) => (
                                <SelectItem key={a.id} value={a.id} data-testid={`item-animal-option-${a.id}`}>
                                  {a.name}{a.animalType ? ` (${a.animalType.toLowerCase()})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-5">
                          <Label className="text-xs">Service</Label>
                          <Select value={it.serviceId} onValueChange={(v) => updateLineItem(idx, { serviceId: v })}>
                            <SelectTrigger data-testid={`item-service-select-${idx}`}><SelectValue placeholder="Pick service" /></SelectTrigger>
                            <SelectContent>
                              {services.map((s) => (
                                <SelectItem key={s.id} value={s.id}>{s.name}{s.priceCents ? ` · $${(s.priceCents/100).toFixed(2)}` : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs">Price</Label>
                          <Input
                            type="number" step="0.01" min="0"
                            placeholder={fallbackPrice ? (fallbackPrice/100).toFixed(2) : '0.00'}
                            value={it.priceCents}
                            onChange={(e) => updateLineItem(idx, { priceCents: e.target.value })}
                            data-testid={`item-price-input-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Per-animal notes (optional)"
                          value={it.notes}
                          onChange={(e) => updateLineItem(idx, { notes: e.target.value })}
                          data-testid={`item-notes-input-${idx}`}
                        />
                        <Button type="button" variant="ghost" size="icon"
                          onClick={() => removeLineItem(idx)}
                          data-testid={`item-remove-${idx}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  );
                })}

                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={addLineItem} data-testid="add-item-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add animal row
                  </Button>
                  {clientMode === 'existing' && existingClientId && (
                    <div className="flex items-center gap-2 ml-auto">
                      <Input
                        placeholder="Quick-add animal name"
                        value={newAnimalName}
                        onChange={(e) => setNewAnimalName(e.target.value)}
                        className="h-8 max-w-[180px]"
                        data-testid="quick-add-animal-name"
                      />
                      <Select value={newAnimalType} onValueChange={setNewAnimalType}>
                        <SelectTrigger className="h-8 w-[110px]" data-testid="quick-add-animal-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ANIMAL_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" size="sm" variant="outline" onClick={quickAddAnimal}
                        disabled={addingAnimal || !newAnimalName.trim()}
                        data-testid="quick-add-animal-btn">
                        {addingAnimal && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                        + Animal
                      </Button>
                    </div>
                  )}
                </div>

                {items.length > 0 && (
                  <div className="text-right text-sm font-semibold pt-1" data-testid="items-total">
                    Total: ${(itemsTotalCents / 100).toFixed(2)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment + notes */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Payment</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UNPAID">Unpaid</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={1} placeholder="Optional appointment notes..." />
            </div>
          </div>

          {/* RW-BUILD-003 — Recurrence */}
          <div className="rounded-lg border p-3 space-y-3" data-testid="recurrence-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={repeat}
                onChange={(e) => { setRepeat(e.target.checked); setRecConflicts([]); }}
                className="h-4 w-4 accent-primary"
                data-testid="recurrence-toggle"
              />
              <span className="font-medium flex items-center gap-1">
                <Repeat className="h-4 w-4" /> Repeat this appointment
              </span>
            </label>

            {repeat && (
              <div className="space-y-3" data-testid="recurrence-fields">
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-xs">Every</Label>
                    <Input type="number" min="1" value={recInterval}
                      onChange={(e) => { setRecInterval(e.target.value); setRecConflicts([]); }}
                      className="h-9 w-20" data-testid="recurrence-interval" />
                  </div>
                  <div>
                    <Label className="text-xs">Unit</Label>
                    <Select value={recUnit} onValueChange={(v) => { setRecUnit(v); setRecConflicts([]); }}>
                      <SelectTrigger className="h-9 w-32" data-testid="recurrence-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DAYS">Days</SelectItem>
                        <SelectItem value="WEEKS">Weeks</SelectItem>
                        <SelectItem value="MONTHS">Months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Ends</Label>
                    <Select value={recEndRule} onValueChange={(v) => { setRecEndRule(v); setRecConflicts([]); }}>
                      <SelectTrigger className="h-9 w-44" data-testid="recurrence-endrule"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="COUNT">After N appointments</SelectItem>
                        <SelectItem value="DATE">On a date</SelectItem>
                        <SelectItem value="OPEN">Continue until stopped</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {recEndRule === 'DATE' ? (
                    <div>
                      <Label className="text-xs">End date</Label>
                      <Input type="date" value={recEndDate}
                        onChange={(e) => { setRecEndDate(e.target.value); setRecConflicts([]); }}
                        className="h-9" data-testid="recurrence-enddate" />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs">{recEndRule === 'OPEN' ? 'Keep planned' : 'How many'}</Label>
                      <Input type="number" min="1" value={recCount}
                        onChange={(e) => { setRecCount(e.target.value); setRecConflicts([]); }}
                        className="h-9 w-24" data-testid="recurrence-count" />
                    </div>
                  )}
                </div>

                {recConflicts.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm" data-testid="recurrence-conflicts">
                    <p className="font-medium text-amber-800 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" /> {recConflicts.length} date(s) conflict:
                    </p>
                    <ul className="mt-1 text-amber-700 list-disc list-inside max-h-28 overflow-y-auto">
                      {recConflicts.map((c, i) => (
                        <li key={i}>{(c.date || '').slice(0, 10)} — {(c.reasons || []).join(', ')}</li>
                      ))}
                    </ul>
                    <p className="mt-1 text-xs text-amber-700">Adjust the dates above, or confirm to create anyway.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {repeat && recConflicts.length > 0 && (
              <Button type="button" variant="secondary" disabled={saving}
                onClick={() => submit(null, true)} data-testid="confirm-conflicts-btn">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Create anyway
              </Button>
            )}
            <Button type="submit" disabled={saving} data-testid="save-appointment-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {repeat ? 'Create series' : 'Save appointment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
